import { useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  MoreHorizontal,
  Layers,
  CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { VariantModal } from './variant-modal';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type Variant = {
  id: string;
  name: string;
  description: string | null;
  code: string | null;
  imageUrl: string | null;
  badgeText: string | null;
  sortOrder: number;
  isDefault: boolean;
  isCalculated: boolean;
  priceAdjustment: number | null;
  options: Array<{
    id: string;
    name: string;
    groupName: string;
    price: number;
    pricingUnit: string;
  }>;
};

interface VariantSectionProps {
  packageId: string;
  onRefresh?: () => void;
}

export function VariantSection({ packageId, onRefresh }: VariantSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState<Variant | undefined>();

  const {
    data: variants,
    isLoading,
    refetch,
  } = trpc.packageOption.listVariants.useQuery(packageId);

  const deleteMutation = trpc.packageOption.deleteVariant.useMutation({
    onSuccess: () => {
      showSuccessToast('Variant deleted');
      refetch();
      onRefresh?.();
    },
    onError: (error) => {
      showErrorToast(error.message || 'Failed to delete variant');
    },
  });

  const handleAdd = () => {
    setEditingVariant(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (variant: Variant) => {
    setEditingVariant(variant);
    setIsModalOpen(true);
  };

  const handleDelete = (variantId: string) => {
    if (confirm('Are you sure you want to delete this variant?')) {
      deleteMutation.mutate(variantId);
    }
  };

  const handleSuccess = () => {
    refetch();
    onRefresh?.();
  };

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold flex items-center gap-2">
          <Layers className="h-5 w-5" />
          Package Variants
        </h2>
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Add Variant
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : variants && variants.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {variants.map((variant) => (
            <div
              key={variant.id}
              className="rounded-lg border p-4 hover:bg-muted/30 transition-colors group relative"
            >
              {/* Badge */}
              {variant.badgeText && (
                <div className="absolute -top-2 -right-2">
                  <Badge className="bg-primary text-primary-foreground text-[10px] px-2">
                    {variant.badgeText}
                  </Badge>
                </div>
              )}

              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{variant.name}</span>
                    {variant.isDefault && (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                  {variant.code && (
                    <span className="text-xs text-muted-foreground">
                      Code: {variant.code}
                    </span>
                  )}
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(variant)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => handleDelete(variant.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {variant.description && (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {variant.description}
                </p>
              )}

              {/* Included Options */}
              {variant.options.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    Includes:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {variant.options.slice(0, 3).map((opt) => (
                      <Badge
                        key={opt.id}
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0"
                      >
                        {opt.name}
                      </Badge>
                    ))}
                    {variant.options.length > 3 && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0"
                      >
                        +{variant.options.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Price info */}
              <div className="mt-3 pt-3 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Pricing:</span>
                  <span className="font-medium">
                    {variant.isCalculated
                      ? 'Calculated from options'
                      : variant.priceAdjustment !== null
                        ? `+${new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          }).format(variant.priceAdjustment)}`
                        : 'Base price'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <Layers className="h-8 w-8 text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">No variants</p>
          <p className="text-xs text-muted-foreground mt-1">
            Create pre-configured variants like "Standard" or "Luxury"
          </p>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <VariantModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          packageId={packageId}
          variant={editingVariant}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}
